import {inject, injectable} from 'inversify';
import {runInAction} from 'mobx';

import {pollAsync} from '@yourcoach/async-utils';

import type {WsMessageModel} from '../WsMessage/WsMessage.model';

import {logger} from '../../../utils/logger';
import {FileConvertWsService} from '../../system/fileConvertWs/FileConvertWs.service';
import {CoreServiceLocator} from '../../system/serviceLocator/CoreServiceLocator';
import {ReadFileUseCase} from '../../useCases/File/ReadFile.uc';
import {UploadFileUseCase} from '../../useCases/File/UploadFile.uc';
import {BaseController} from '../Base/Base.controller';
import {FileModel} from './File.model';

@injectable()
export class FileController extends BaseController<FileModel> {
  constructor(
    @inject(UploadFileUseCase) private readonly createFile: UploadFileUseCase,
    @inject(ReadFileUseCase) private readonly readFile: ReadFileUseCase,
    @inject(CoreServiceLocator)
    private readonly serviceLocator: CoreServiceLocator,
  ) {
    super();
  }

  async handlerWsUpdate(
    model: FileModel,
    event: WsMessageModel,
  ): Promise<void> {
    const file = event.file;

    if (!file) {
      return;
    }

    const result = await this.readFile.exec({
      fileId: file.id,
    });

    model.update(result);
  }

  registerWs(model: FileModel): void {
    const shouldRegister = model.fileType === FileModel.Type.AUDIO;

    if (shouldRegister) {
      this.serviceLocator
        .get(FileConvertWsService)
        .registerFileToConvert(model);
    }
  }

  async upload(model: FileModel, config: FileModel.CreateConfig) {
    if (model.isUploaded) {
      return;
    }

    runInAction(() => {
      model._isUploading.setTrue();
      model._fileConfig = config;
    });

    try {
      const response = await this.createFile.exec(config);

      runInAction(() => {
        model.update(response);
      });

      this.registerWs(model);
    } catch (e) {
      logger.error(e);

      return;
    } finally {
      model._isUploading.setFalse();
    }
  }

  async uploadIfNeeded(model: FileModel): Promise<FileModel> {
    if (model.isUploaded) {
      return model;
    }

    if (model._isUploading.isTrue) {
      await pollAsync(() => model._isUploading.isFalse);
    } else if (model._fileConfig) {
      await this.upload(model, model._fileConfig);
    }

    return model;
  }
}
